// Accordion Panel Component
class AccordionPanel extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.render();
  }

  connectedCallback() {
    // Set default type if not specified
    if (!this.hasAttribute("type")) {
      this.setAttribute("type", "single");
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
                    <slot></slot>
                `;
  }

  // Helper method to check if multiple items can be open
  get allowMultiple() {
    return this.getAttribute("type") === "multiple";
  }

  static get observedAttributes() {
    return ["type"];
  }
}

// Accordion Item Component
class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.isOpen = false;
    this.render();
    this.setupEventListeners();
  }

  connectedCallback() {
    if (!this.hasAttribute("data-accordion-state")) {
      this.setAttribute("data-accordion-state", "closed");
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
                    <slot></slot>
                `;
  }

  setupEventListeners() {
    this.addEventListener("accordion-toggle", this.handleToggle.bind(this));
  }

  handleToggle(event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    this.setAttribute("data-accordion-state", this.isOpen ? "open" : "closed");

    // Update content visibility
    const content = this.querySelector("accordion-content");
    if (content) {
      content.setAttribute(
        "data-accordion-state",
        this.isOpen ? "open" : "closed"
      );
    }

    // Close other items in the same panel only if type="single" (default behavior)
    const panel = this.closest("accordion-panel");
    if (panel && this.isOpen && !panel.allowMultiple) {
      const otherItems = panel.querySelectorAll("accordion-item");
      otherItems.forEach((item) => {
        if (item !== this && item.isOpen) {
          item.dispatchEvent(new CustomEvent("accordion-toggle"));
        }
      });
    }
  }
}

// Accordion Trigger Component
class AccordionTrigger extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.render();
    this.setupEventListeners();
  }

  render() {
    this.shadowRoot.innerHTML = `
                    <style>
                        .trigger-button {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 0.5rem;
                            width: 100%;
                            padding: var(--trigger-padding, 1rem 0);
                            font-family: inherit;
                            font-size: var(--trigger-font-size, 0.875rem);
                            line-height: var(--trigger-line-height, 1.25rem); 
                            font-weight: var(--trigger-font-weight, 500);
                            text-align: left;
                            background: transparent;
                            border: none;
                            cursor: pointer;
                            transition: all 0.2s ease;
                            color: inherit;
                        }
                        
                        .trigger-button:hover {
                            background-color: var(--trigger-hover-bg,rgba(255, 255, 255, 0));
                        }
                        
                        .trigger-button:focus-visible {
                            outline: 2px solid var(--trigger-focus-color, #3b82f6);
                            outline-offset: -2px;
                        }
                        
                        .chevron {
                            width: var(--chevron-size, 16px);
                            height: var(--chevron-size, 16px);
                            transition: transform 0.2s ease;
                            flex-shrink: 0;
                            color: inherit;
                        }
                        
                        :host-context(accordion-item[data-accordion-state="open"]) .chevron {
                            transform: rotate(180deg);
                        }
                        
                        .trigger-text {
                            flex: 1;
                        }
                    </style>
                    
                    <button class="trigger-button" type="button" aria-expanded="false">
                        <span class="trigger-text">
                            <slot></slot>
                        </span>
                        <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="m6 9 6 6 6-6"/>
                        </svg>
                    </button>
                `;
  }

  setupEventListeners() {
    const button = this.shadowRoot.querySelector(".trigger-button");
    button.addEventListener("click", this.handleClick.bind(this));
    button.addEventListener("keydown", this.handleKeyDown.bind(this));

    // Listen for state changes from parent item
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-accordion-state"
        ) {
          const item = this.closest("accordion-item");
          const isOpen =
            item && item.getAttribute("data-accordion-state") === "open";
          button.setAttribute("aria-expanded", isOpen.toString());
        }
      });
    });

    const item = this.closest("accordion-item");
    if (item) {
      observer.observe(item, { attributes: true });
    }
  }

  handleClick() {
    const item = this.closest("accordion-item");
    if (item) {
      item.dispatchEvent(new CustomEvent("accordion-toggle"));
    }
  }

  handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.handleClick();
    }
  }
}

// Accordion Content Component
class AccordionContent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.render();
  }

  connectedCallback() {
    // Set initial state
    this.setAttribute("data-accordion-state", "closed");
  }

  render() {
    this.shadowRoot.innerHTML = `
                    <style>
                      .content-wrapper {
                        display: flex;
                        flex-direction: column;
                        gap: var(--content-gap, 1rem);
                        padding: var(--content-padding, 0 0 1rem 0);
                      }
                    </style>

                    <div class="content-wrapper">
                      <slot></slot>
                    </div>
                `;
  }

  static get observedAttributes() {
    return ["data-accordion-state"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-accordion-state" && oldValue !== newValue) {
      // Calculate the actual content height
      if (newValue === "open") {
        // Temporarily set height to auto to measure content
        this.style.height = "auto";
        const height = this.scrollHeight;
        this.style.setProperty("--accordion-content-height", `${height}px`);
        this.style.height = ""; // Reset to let CSS handle it
      } else if (newValue === "closed" && oldValue === "open") {
        // Set the height before closing animation
        const height = this.scrollHeight;
        this.style.setProperty("--accordion-content-height", `${height}px`);
        // Remove the variable after animation completes
        setTimeout(() => {
          this.style.removeProperty("--accordion-content-height");
        }, 300);
      }
    }
  }
}

// Register the custom elements
customElements.define("accordion-panel", AccordionPanel);
customElements.define("accordion-item", AccordionItem);
customElements.define("accordion-trigger", AccordionTrigger);
customElements.define("accordion-content", AccordionContent);
